import { useAuth } from '@beukay/auth-mall';
import type { User } from '@beukay/auth-mall';
import $trace from '@beukay/trace';
import YSF from '@neysf/qiyu-web-sdk';
import { useAsyncEffect } from 'ahooks';
import { useSetAtom } from 'jotai';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { queryAreasInfoAtom, shopCodeAtom, userAtom } from '@/atoms';
import { getShopInfo } from '@/services/api';
import { LOCALE } from '@/utils';
import { TRACE_APP_ID } from '@/utils/constant';

const Preloader: FC<{ children?: ReactNode }> = ({ children }) => {
  const { getUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const setShopCode = useSetAtom(shopCodeAtom);
  const setUser = useSetAtom(userAtom);
  const queryAreasInfo = useSetAtom(queryAreasInfoAtom);

  const initYsf = (user: User) => {
    YSF.init(
      '22c0993b77ac64d149158bd30de70aba',
      {
        hidden: 1,
      },
      'online',
    )
      .then((ysf) => {
        ysf('config', {
          uid: user?.userId ? `${user?.userId}-${APP_ENV}` : undefined,
          name: user?.username,
          staffid: user?.serviceId,
          groupid: user?.serviceGroupId,
          language: LOCALE.getLocale() === 'zh_CN' ? 'zh-cn' : 'en',
          robotShuntSwitch: 1,
          data: JSON.stringify(
            user?.username
              ? [
                  { key: 'real_name', value: user?.username },
                  {
                    index: 0,
                    key: 'userId',
                    label: '用户ID',
                    value: String(user?.userId),
                  },
                  {
                    index: 1,
                    key: 'customerId',
                    label: '客户ID',
                    value: String(user?.customerId),
                  },
                  {
                    index: 2,
                    key: 'customerCode',
                    label: '客户编码',
                    // @ts-ignore
                    value: String(user?.customerCode),
                  },
                  {
                    index: 3,
                    key: 'username',
                    label: '用户名',
                    value: user?.username,
                  },
                  {
                    index: 4,
                    key: 'customerName',
                    label: '客户名称',
                    value: user?.customerName,
                  },
                  {
                    index: 5,
                    key: 'businessModel',
                    label: '业务模式',
                    value: user?.businessModel?.join(','),
                  },
                  {
                    index: 6,
                    label: '标签',
                    value: user?.businessModel?.join(','),
                    key: 'tags',
                  },
                ]
              : [],
          ),
          success: function () {
            // 成功回调
            window.BEUKAY_YSF = ysf;
          },
        });
      })
      .catch((error) => {
        console.log('sdk加载失败---', error);
      });
  };

  const initTrace = () => {
    $trace.init({ app_id: TRACE_APP_ID, enable_stay_duration: true });
  };

  useAsyncEffect(async () => {
    try {
      queryAreasInfo();
      initTrace();
      const user = await getUser();
      setUser(user);
      initYsf(user);
      $trace.track('config', {
        url_domain: `${window.location.origin}${window.location.pathname}`,
        url_domain_parameter: window.location.href,
        page_title: document.title,
      });
      if (user?.userId) {
        $trace.track('config', {
          user_unique_id: user.userId,
        });
      }
      const res = await getShopInfo();
      const { shopCode } = res?.data || {};
      if (shopCode) {
        setShopCode(shopCode);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  return !loading ? <>{children}</> : null;
};

export default Preloader;
